import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { docIdFor, isLeaderboardConfigured } from '@/leaderboard/config';
import { flagSrc } from '@/leaderboard/country';
import type { LeaderboardEntry, UserBestScore } from '@/leaderboard/types';
import { useEffect, useState } from 'react';

interface LeaderboardModalProps {
  open: boolean;
  onClose: () => void;
  personalBest: number;
  playerId: string;
}

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; top: LeaderboardEntry[]; mine: UserBestScore | null }
  | { kind: 'error'; message: string }
  | { kind: 'offline' };

export function LeaderboardModal({ open, onClose, personalBest, playerId }: LeaderboardModalProps) {
  const [state, setState] = useState<LoadState>({ kind: 'idle' });

  useEffect(() => {
    if (!open) return;
    if (!isLeaderboardConfigured()) {
      setState({ kind: 'offline' });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    import('@/leaderboard/leaderboardService')
      .then(({ getTopScores, getUserBestScore }) =>
        Promise.all([getTopScores(100), getUserBestScore(playerId)]),
      )
      .then(([top, mine]) => {
        if (cancelled) return;
        setState({ kind: 'loaded', top, mine });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Load failed.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [open, playerId]);

  return (
    <Modal open={open} onClose={onClose} title="Leaderboard">
      <PersonalBestCard state={state} personalBest={personalBest} playerId={playerId} />
      <LeaderboardBody state={state} playerId={playerId} />
    </Modal>
  );
}

function PersonalBestCard({
  state,
  personalBest,
  playerId,
}: {
  state: LoadState;
  personalBest: number;
  playerId: string;
}) {
  const myDocId = docIdFor(playerId);
  const myEntry =
    state.kind === 'loaded'
      ? (state.top.find((e) => e.id === myDocId) ?? state.mine?.entry ?? null)
      : null;
  const myRank =
    state.kind === 'loaded'
      ? state.top.findIndex((e) => e.id === myDocId) >= 0
        ? state.top.findIndex((e) => e.id === myDocId) + 1
        : (state.mine?.rank ?? 0)
      : 0;

  return (
    <section className="leaderboard-pb-card" aria-label="Your personal best">
      <div className="leaderboard-pb-card__head">
        <span className="leaderboard-pb-card__label">Your best</span>
        {myRank > 0 && (
          <span className="leaderboard-pb-card__rank">
            Rank <strong>#{myRank.toLocaleString()}</strong>
          </span>
        )}
      </div>
      <div className="leaderboard-pb-card__body">
        {myEntry && <Flag cc={myEntry.countryCode} className="leaderboard-pb-card__flag" />}
        <span className="leaderboard-pb-card__score">
          {personalBest > 0 ? personalBest.toLocaleString() : '—'}
        </span>
      </div>
    </section>
  );
}

function Flag({ cc, className }: { cc: string; className?: string }) {
  const src = flagSrc(cc);
  const cls = ['leaderboard-flag', className].filter(Boolean).join(' ');
  if (!src) {
    return (
      <span className={cls} role="img" aria-label={cc} title={cc}>
        🏳
      </span>
    );
  }
  return (
    <img
      className={cls}
      src={src}
      alt={cc}
      title={cc}
      width={22}
      height={16}
      loading="lazy"
      decoding="async"
    />
  );
}

function LeaderboardBody({ state, playerId }: { state: LoadState; playerId: string }) {
  if (state.kind === 'idle' || state.kind === 'loading') {
    return <LeaderboardSkeleton />;
  }
  if (state.kind === 'offline') {
    return <p className="leaderboard-status">Leaderboard not configured for this build.</p>;
  }
  if (state.kind === 'error') {
    return (
      <p className="leaderboard-status leaderboard-status--err">
        Couldn’t load leaderboard ({state.message}).
      </p>
    );
  }
  if (state.top.length === 0) {
    return (
      <div className="leaderboard-empty">
        <span className="leaderboard-empty__icon" aria-hidden="true">
          🏆
        </span>
        <p className="leaderboard-status">No scores yet — be the first to submit!</p>
      </div>
    );
  }
  const myDocId = docIdFor(playerId);
  const inTop = state.top.some((e) => e.id === myDocId);
  return (
    <div className="leaderboard-list">
      <ol className="leaderboard-rows" aria-label="Top scores">
        {state.top.map((entry, index) => {
          const rank = index + 1;
          const podiumClass =
            rank === 1
              ? 'leaderboard-row--gold'
              : rank === 2
                ? 'leaderboard-row--silver'
                : rank === 3
                  ? 'leaderboard-row--bronze'
                  : '';
          return (
            <li
              key={entry.id}
              className={`leaderboard-row ${podiumClass}`}
              data-mine={entry.id === myDocId ? 'true' : undefined}
            >
              <span className="leaderboard-rank" data-podium={rank <= 3 ? rank : undefined}>
                {rank}
              </span>
              <Flag cc={entry.countryCode} />
              <span className="leaderboard-name">{entry.username}</span>
              <span className="leaderboard-score">{entry.score.toLocaleString()}</span>
            </li>
          );
        })}
      </ol>
      {state.mine && !inTop && (
        <div className="leaderboard-pinned" aria-label="Your row">
          <span className="leaderboard-rank">
            {state.mine.rank > 0 ? `#${state.mine.rank}` : '—'}
          </span>
          <Flag cc={state.mine.entry.countryCode} />
          <span className="leaderboard-name">{state.mine.entry.username}</span>
          <span className="leaderboard-score">{state.mine.entry.score.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}

function LeaderboardSkeleton() {
  return (
    <output className="leaderboard-skeleton" aria-label="Loading top 100">
      <div className="leaderboard-skeleton__spinner">
        <Spinner size="md" label="Loading top 100" />
      </div>
      <ol className="leaderboard-rows" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <li key={i} className="leaderboard-row leaderboard-row--skeleton">
            <span className="leaderboard-rank" />
            <span className="leaderboard-flag" />
            <span className="leaderboard-name" />
            <span className="leaderboard-score" />
          </li>
        ))}
      </ol>
    </output>
  );
}
